import { type FocusEvent, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import SliderOpenx from '@/app/components/search/components/Slider/index.tsx';
import { focusRing } from '@/app/components/search/styles/mixins.ts';
import { debounce } from '@/utils/debounce.ts';

export type RangeValue = { min: number; max: number };

type RangeSliderProps = {
  value: RangeValue;
  from: number;
  to: number;
  disabled: boolean;
  step: number;
  onDrag: (value: RangeValue) => void;
  onCommit: (value: RangeValue) => void;
};

const COMMIT_FALLBACK_MS = 50;
const KEYBOARD_SETTLE_MS = 500;

// Hides the design system's value row and restyles it; only Radix's root carries dir, unlike the track.
const SliderShell = styled.div`
  width: 100%;

  .slider,
  .slider > div {
    background-color: transparent;
  }

  .slider > div > div:first-child {
    display: none;
  }

  [dir][data-orientation] {
    height: 28px;
  }

  [dir][data-orientation] > [data-orientation] {
    background-color: ${({ theme }) => theme.kallisto.color.background.static.backgroundDisabled};
  }

  /* Disabled is left to the design system, the one state the design does not cover. */
  [role='slider']:not([data-disabled]) {
    box-sizing: border-box;
    background-color: ${({ theme }) => theme.kallisto.color.background.static.backgroundPage};
    border: 2px solid ${({ theme }) => theme.kallisto.color.border.interactive.borderPrimaryDefault};
    box-shadow:
      0 2px 12px ${({ theme }) => theme.kallisto.color.common.black[50]},
      0 12px 16px ${({ theme }) => theme.kallisto.color.common.black[50]};
  }

  [role='slider']:focus-visible {
    ${focusRing}
  }
`;

export const RangeSlider = ({ value, from, to, disabled, step, onDrag, onCommit }: RangeSliderProps) => {
  const pendingRef = useRef<RangeValue | null>(null);
  const settleRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const commitPending = () => {
    const pending = pendingRef.current;
    if (!pending) return;
    pendingRef.current = null;
    if (pending.min !== value.min || pending.max !== value.max) onCommit(pending);
  };
  const commitPendingRef = useRef(commitPending);
  commitPendingRef.current = commitPending;

  // Radix commits on every arrow key, so a run of them settles into one search.
  const settle = useMemo(() => debounce(() => commitPendingRef.current(), KEYBOARD_SETTLE_MS), []);
  useEffect(
    () => () => {
      settle.cancel();
      clearTimeout(settleRef.current);
    },
    [settle]
  );

  const readValues = (changed: number[] | number): RangeValue =>
    Array.isArray(changed) ? { min: changed[0], max: changed[1] } : { min: from, max: changed };

  const handleChange = (changed: number[] | number) => {
    const next = readValues(changed);
    pendingRef.current = next;
    onDrag(next);
  };

  const handleCommittedChange = (changed: number[] | number) => {
    pendingRef.current = readValues(changed);
    settle();
  };

  // A pointer gesture has a definite end, and this also covers Radix missing onValueCommit on Chrome.
  const commitOnRelease = () => {
    settle.cancel();
    settleRef.current = setTimeout(() => commitPendingRef.current(), COMMIT_FALLBACK_MS);
  };

  // Apply can follow the last key straight away, so leaving the slider skips the settle.
  const commitOnLeave = (event: FocusEvent<HTMLDivElement>) => {
    if (event.relatedTarget instanceof Node && event.currentTarget.contains(event.relatedTarget)) return;
    settle.cancel();
    commitPendingRef.current();
  };

  return (
    <SliderShell onPointerUp={commitOnRelease} onBlur={commitOnLeave} data-testid="range-slider">
      <SliderOpenx
        value={{ min: value.min, minAllowed: from, max: value.max, maxAllowed: to, disabled }}
        disabled={disabled}
        handleChange={handleChange}
        handleCommittedChange={handleCommittedChange}
        formatter={(price) => `${price}`}
        hideMin={from === to}
        step={step}
      />
    </SliderShell>
  );
};
